import { v, type Infer } from "convex/values";
import { entityTypeValidator } from "../schemas/validators/enums";

export const PROFILE_REQUIREMENT_CODES = {
  PROFILE_NAME: "PROFILE_NAME",
  PROFILE_EMAIL: "PROFILE_EMAIL",
  PROFILE_PHONE: "PROFILE_PHONE",
  PROFILE_EIN: "PROFILE_EIN",
  ADDRESS_SHIPPING: "ADDRESS_SHIPPING",
  BILLING_SETUP: "BILLING_SETUP",
} as const;

export type ProfileRequirementCode =
  (typeof PROFILE_REQUIREMENT_CODES)[keyof typeof PROFILE_REQUIREMENT_CODES];

export const profileRequirementCodeValidator = v.union(
  v.literal("PROFILE_NAME"),
  v.literal("PROFILE_EMAIL"),
  v.literal("PROFILE_PHONE"),
  v.literal("PROFILE_EIN"),
  v.literal("ADDRESS_SHIPPING"),
  v.literal("BILLING_SETUP"),
);

export const profileRequirementValidator = v.object({
  code: profileRequirementCodeValidator,
  label: v.string(),
  description: v.string(),
  actionUrl: v.string(),
  actionLabel: v.string(),
  completed: v.boolean(),
  priority: v.union(v.literal("required"), v.literal("recommended")),
});

export type ProfileRequirement = Infer<typeof profileRequirementValidator>;

export const profileStatusValidator = v.union(
  v.literal("incomplete"),
  v.literal("complete"),
);

export type ProfileStatus = Infer<typeof profileStatusValidator>;

export const profileStateValidator = v.object({
  status: profileStatusValidator,

  requirements: v.array(profileRequirementValidator),
  completed: v.array(profileRequirementCodeValidator),
  pending: v.array(profileRequirementCodeValidator),

  entityType: entityTypeValidator,

  completedCount: v.number(),
  totalCount: v.number(),
  progressPercent: v.number(),
});

export type ProfileState = Infer<typeof profileStateValidator>;

export const profileReadinessValidator = v.object({
  status: profileStatusValidator,
  requirements: v.array(profileRequirementValidator),
  completedCount: v.number(),
  totalCount: v.number(),
  progressPercent: v.number(),
});

export const companyReadinessValidator = v.object({
  isComplete: v.boolean(),
  billingCapable: v.boolean(),
  canPublish: v.boolean(),
  freeListingsOnly: v.boolean(),
  billingStatus: v.optional(
    v.union(
      v.literal("stripe_connect"),
      v.literal("platform_managed"),
      v.literal("free_listings"),
    ),
  ),
  profile: profileReadinessValidator,
  payouts: v.object({
    underReview: v.boolean(),
    canManageBilling: v.boolean(),
  }),
});

export type CompanyReadiness = Infer<typeof companyReadinessValidator>;
